<script setup async>
import {onMounted, onBeforeUnmount, ref, computed} from "vue";
import {createError} from 'nuxt/app';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useMessaging} from '~/composables/useMessaging.js';
import {useMailer} from '~/composables/useMailer.js';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ActionMenuItem from "~/components/navigation/ActionMenuItem.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import VisitorPreviewBanner from "~/components/VisitorPreviewBanner.vue";
import {clubFacts} from "~/utils/privateAccess";
import AccessApplicationCell from "~/components/users/AccessApplicationCell.vue";
import JoinRequestPending from "~/components/join/JoinRequestPending.vue";
import NoResults from "~/components/loaders/NoResults.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import LeagueClickableCell from "~/components/lists/LeagueClickableCell.vue";
import LadderClickableCell from "~/components/lists/LadderClickableCell.vue";
import MeetClickableCell from "~/components/lists/MeetClickableCell.vue";
import AmericanoClickableCell from "~/components/lists/AmericanoClickableCell.vue";
import PlayerList from "~/components/players/PlayerList.vue";
import TournamentClickableCell from "~/components/lists/TournamentClickableCell.vue";
import AvatarImageLoader from "~/components/loaders/AvatarImageLoader.vue";
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import {useRuntimeConfig, useNuxtApp} from "nuxt/app";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import PlayerCard from "~/components/players/PlayerCard.vue";
import MembershipTypeRow from "~/components/memberships/MembershipTypeRow.vue";
import ClubRightNowCard from "~/components/clubs/ClubRightNowCard.vue";
import TlDialog from "~/components/TlDialog.vue";
import MyBookingsList from "~/components/bookings/MyBookingsList.vue";
import {countNoun} from "~/utils/adminActionCopy";
import {useBookingsMember} from "~/composables/useBookingsMember";
import {personalBookings, splitBookings, takesBookings} from "~/utils/bookingsMember";
import {whenHydrated} from "~/utils/queryFlags";
import {CLUB_PLAYERS_FIRST, clubEntries, clubPageKey, clubShowAllowed, clubShowFromQuery, clubShowQuery, competitionCount, competitionGroups, isVisitorPreview, membershipTypeTo, myMembershipTo, playersToShow, showAllPlayersLabel, showingFirstLine, shownEntryGone} from "~/utils/clubPage";
import {useMembershipsMember} from "~/composables/useMembershipsMember";
import {membershipChoiceMessage} from "~/composables/useLeaveEntity";
import {DISPLAY_AS} from "~/utils/memberships";
import {useClubPlayers} from "~/composables/useClubPlayers";
import {playersAccessNotice} from "~/utils/clubPlayers";
import {useVisitorPreviewPage} from "~/composables/useVisitorPreview";

definePageMeta({
  name: 'ClubDetail',
  path: '/clubs/:clubId',
  // The visitor preview is another viewer: keyed on ?viewAs so entering and leaving it remount the page and every
  // piece is asked for again, as a fresh load would (utils/clubPage.clubPageKey; ?show= is not in the key).
  key: clubPageKey,
});
const {t} = useI18n()
const route = useRoute();
const clubId = route.params.clubId;

const {item: page, getItem: getPage, hasItemErrors: hasPageErrors} = useCrudApi("api/v2/page/club");
// ?viewAs=visitor (the admin dashboard's "Public page" tile): an admin sees what a visitor sees. The page is keyed
// on it (definePageMeta above), so this never changes within one mount: "Back to my view" and the tile both land
// on a fresh page whose setup, mount-time loads and child components all belong to the new viewer (X70, Grant
// 2026-10-20). While it is on, EVERY request this page and its children make (page/club, the availability card,
// the recent results, anything a child asks for) carries X-View-As: visitor and the API serves it signed out, and
// the children that gate on a sign-in (result rows, the player sheet, the availability card) read the same flag
// (useVisitorPreview, X73): the two request transports add the header from the flag, so nothing here passes
// headers by hand, and the page is what a signed-out visitor gets.
const visitorPreview = computed(() => isVisitorPreview(route.query));
useVisitorPreviewPage((r) => isVisitorPreview(r?.query));
await getPage(route.params.clubId);
if (hasPageErrors.value) {
  throw createError({
    statusCode: 404, // or another relevant status code
    message: 'Club not found'
  });
}

const {setMeta} = useHeadService();
setMeta(
    page.value?.club?.name + ' · Club' + (page.value?.club?.hasFixedAddress ? ' based in ' + page.value?.club?.city : '') + ' on Tap Leagues',
    page.value?.club?.name + ' is a club' + (page.value?.club?.hasFixedAddress ? ' based in ' + page.value?.club?.city : '') + ' on Tap Leagues. Find out more about ' + page.value?.club?.name + ' and request to join through Tap Leagues.'
);
const {isAdminOfClub, isMemberOfClub, isAuthenticated, user} = useAuthService();
const router = useRouter();
const {filtersToQuery} = useMailer();
const {leaveClub, leavePending} = useLeaveEntity();
// In the visitor preview the admin is shown the page as an outsider: no admin or
// member controls, the visitor's join banner and the visitor's member list.
const clubAdmin = computed(() => !visitorPreview.value && isAdminOfClub(page.value?.club?.id));
const clubMember = computed(() => !visitorPreview.value && isMemberOfClub(page.value?.club?.id));

// The club has been deleted (archived) - show the deleted notice instead of content.
const clubArchived = computed(() => !!page.value?.club?.archivedAt);
const clubPrivate = computed(() => !!page.value?.privateToViewer);
// TEST_T2 M7: whether the availability card found bookings on (it asks the API itself).
const rightNowOn = ref(false);
usePrivatePage(clubPrivate);

// Filter join link through the same visibility rules as the other competition pages
const visibleJoinLink = computed(() => {
  const raw = page.value?.club?.visibleInviteLink;
  if (!raw) return null;
  if (clubAdmin.value) return raw;

  // Non-admins require: visible (0), active, and scope-allowed. The visitor preview
  // still gets the admin's copy of the link, so it also drops one shown to members only (2),
  // which the API never sends a real visitor.
  const scopeAllowed = raw.inviteScope !== 1 || clubMember.value;
  const pageAllowed = visitorPreview.value ? raw.inviteEntityPageSetting === 0 : raw.inviteEntityPageSetting !== 1;
  return pageAllowed && raw.isActive && scopeAllowed
      ? raw : null;
});

// Does the visible join link auto-join, or require admin approval?
const autoJoin = computed(() => visibleJoinLink.value?.inviteJoinSetting === 0);

// The user's own pending join request for this club (from the auth user object).
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('club', computed(() => page.value?.club?.id));

// Static map for the location card (Google Static Maps; tapping opens directions).
const config = useRuntimeConfig();
const mapsApiKey = config.public?.grantThomasNuxt?.googleMapsApiKey;
const clubLocation = computed(() => page.value?.club?.location);
const staticMapUrl = computed(() => {
  const loc = clubLocation.value;
  if (!loc || !mapsApiKey) return null;
  return `https://maps.googleapis.com/maps/api/staticmap?center=${loc.latitude},${loc.longitude}`
      + `&zoom=15&size=640x300&scale=2&markers=color:0xE2231A%7C${loc.latitude},${loc.longitude}&key=${mapsApiKey}`;
});
const directionsUrl = computed(() => {
  const loc = clubLocation.value;
  if (!loc) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;
});

// Members list (Grant 2026-10-20): the first 50 players are rendered (on the server too), "Show all N players"
// reveals the rest here in the browser (page/club already holds everyone, so nothing is fetched again), and the
// search runs over ALL players, not the 50 shown. 801 PlayerCards in one server render held ~440 MB (MEMORY_M4);
// 50 hold ~25 MB. The header's count is the club's own (page.club.playerCount), untouched by the cut.
const memberSearch = ref('');
const showAllPlayers = ref(false);
// X81 (R28 P4): the whole row opens the player dialog (PlayerCard's own click covers only the avatar and name), by keyboard too;
// a click inside the card, or on a link or button in the row, is left to them.
const memberCards = new Map();
const setMemberCard = (id, el) => { if (el) memberCards.set(id, el); else memberCards.delete(id); };
const openMemberRow = (e, id) => {
  if (e.type === 'click' && e.target.closest('.club-member-name-col, a, button')) return;
  memberCards.get(id)?.open();
};
const membersView = computed(() => playersToShow(page.value?.players, {query: memberSearch.value, showAll: showAllPlayers.value}));
const filteredMembers = computed(() => membersView.value.matches);
const shownMembers = computed(() => membersView.value.shown);
const membersCapped = computed(() => membersView.value.capped);
const memberSearching = computed(() => String(memberSearch.value ?? '').trim() !== '');
const showAllLabel = computed(() => showAllPlayersLabel(filteredMembers.value.length, memberSearching.value));
const showingLine = computed(() => showingFirstLine(shownMembers.value.length, filteredMembers.value.length));

// Messaging and mailer composables
const {entityChatLoading, createOrOpenEntityChat} = useMessaging();

// Chat handler function
const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {clubId: page.value.club.id, conversationId: page.value.club.conversationId},
      () => getPage(route.params.clubId)
  );
};

// Handler for leaving the club. ME25: with an auto-renewing membership the
// API first asks what happens to it; the dialog then offers the two choices.
const leaveMembershipChoice = ref(null); // the API's words, when it asked
// "Keep them and leave" when the API named several memberships (RETEST_R1 Low 2).
const leaveChoicePronoun = computed(() => /\bkeep them\b/i.test(leaveMembershipChoice.value || '') ? 'them' : 'it');
const handleLeaveClub = async (membershipAction = null) => {
  try {
    await leaveClub(page.value.club.id, membershipAction);
    showLeaveClubDialog.value = false;
    leaveMembershipChoice.value = null;
    // Refresh page to reflect changes
    await getPage(route.params.clubId);
  } catch (error) {
    const choice = membershipChoiceMessage(error);
    if (choice) { leaveMembershipChoice.value = choice; return; }
    console.error('Failed to leave club:', error);
    // Keep dialog open on error so user can see the error message
  }
};

const showLeaveClubDialog = ref(false);
watch(showLeaveClubDialog, (open) => { if (!open) leaveMembershipChoice.value = null; });

// ── Memberships & passes (D31): the Live, visible types, for signed-in viewers.
// The list endpoint needs a sign-in, so a visitor sees the card and its CTA
// only (the list page asks them to sign in).
const {listClubTypes, mine: myMemberships} = useMembershipsMember();
const membershipTypes = ref([]);
// Grant 2026-11-02: the viewer's current memberships & passes at this club, so the entry and its list say which is theirs.
const heldMemberships = ref([]);
// X219 (R89 P-1): false until the first answer, so the entry's place is held (SSR included) instead of popping in.
const membershipTypesLoaded = ref(false);
const membershipsOn = computed(() => !!page.value?.club?.membershipsEnabled && !clubArchived.value && !clubPrivate.value);
// X221 (R90 N-1): false when the club has no live, public type (the API says so in SSR); null from an older API.
const hasLiveMembershipTypes = computed(() => typeof page.value?.hasLiveMembershipTypes === 'boolean' ? page.value.hasLiveMembershipTypes : null);
const loadMembershipTypes = async () => {
  if (!membershipsOn.value || !isAuthenticated.value || visitorPreview.value || hasLiveMembershipTypes.value === false) { membershipTypes.value = []; membershipTypesLoaded.value = true; return; }
  try {
    const [types, held] = await Promise.all([listClubTypes(page.value.club.id), myMemberships().catch(() => [])]);
    membershipTypes.value = types;
    heldMemberships.value = (held ?? []).filter(m => m.isCurrent && m.clubId === page.value.club.id);
  } catch { membershipTypes.value = []; } finally { membershipTypesLoaded.value = true; }
};

// D47: a club can show its players only to people with a membership or pass. page/club
// says playersLocked (players come back empty); clubPlayers/{id}/access adds the grace
// line and the types on offer. Admins always see everyone, so they're never asked.
const {access: loadPlayersAccess} = useClubPlayers();
const playersAccess = ref(null);
const loadAccess = async () => {
  playersAccess.value = null;
  if (!membershipsOn.value || !isAuthenticated.value || visitorPreview.value || clubAdmin.value) return;
  try { playersAccess.value = await loadPlayersAccess(page.value.club.id); } catch { playersAccess.value = null; }
};
const playersNotice = computed(() => (visitorPreview.value ? {state: 'open', line: ''} : playersAccessNotice(playersAccess.value, page.value)));
const playersLocked = computed(() => playersNotice.value.state === 'locked');
const typesOnOffer = computed(() => playersAccess.value?.typesOnOffer ?? []);
const membershipsBlurb = computed(() => {
  const names = membershipTypes.value.map(t => t.name);
  const passes = membershipTypes.value.filter(t => t.displayAs === DISPLAY_AS.pass).length;
  // Grant 2026-11-02: never "Join the club": the player is already in it, they just don't have a membership.
  if (!names.length) return 'Get a membership or a pass.';
  if (passes && passes < names.length) return 'Get a membership or a pass. Both can mean cheaper meets and entry to competitions.';
  if (passes) return 'Get a pass for cheaper meets and entry to competitions.';
  return 'Get a membership for cheaper meets and entry to competitions.';
});

// X45: a signed-in player's bookings coming up at this club, for the "Your bookings" entry and its dialog.
// Asked once the availability card has found bookings on (one call, only then).
const bookingsApi = useBookingsMember();
const myBookings = ref([]);
const loadMyBookings = async () => {
  if (!rightNowOn.value || !isAuthenticated.value || visitorPreview.value || !(clubMember.value || clubAdmin.value)) { myBookings.value = []; return; }
  // R21 N9: a club booking an admin made is the club's, not theirs: not in Your bookings, not in its count.
  try { myBookings.value = splitBookings(personalBookings(await bookingsApi.mine({clubId: page.value.club.id, includePast: false}))).upcoming; } catch { myBookings.value = []; }
};

// X222 (R91 O-1): today's availability is asked HERE, before the page renders (SSR included), and handed to the
// card. Before, only the card asked once mounted, so on desktop "Playing area availability" arrived after hydration
// in the right column and pushed Recent results down (CLS 0.024-0.034 at 1024-1440). One call as before: the card
// uses this copy instead of asking again; `at` is the server's moment, so the tiles hydrate with the same words.
const {data: firstAvailability} = await useAsyncData(`club-availability-${clubId}-${visitorPreview.value ? 'visitor' : 'viewer'}`, async () => {
  if (!page.value?.club?.id || page.value.club.archivedAt) return null;
  try { return {availability: await bookingsApi.availability(page.value.club.id, {days: 1}), at: Date.now()}; } catch { return null; }
});
if (firstAvailability.value?.availability) rightNowOn.value = takesBookings(firstAvailability.value.availability);

// X223 (R92-L1): the viewer's own bookings are asked here too, before the page renders, so "Your bookings" is in the
// server's HTML instead of arriving after hydration and pushing the page down (~81px, CLS 0.03-0.046). Per viewer:
// bookings/mine is the signed-in user's own list (never the API's shared Crud cache), and the payload key carries the
// viewer's id, so one viewer's copy can never be reused for another. The watch below keeps reloading it on the client
// (the card finding bookings on later, a change in the dialog).
const myBookingsKey = `club-my-bookings-${clubId}-${visitorPreview.value ? 'visitor' : (user.value?.id ?? 'anon')}`;
const {data: firstMyBookings} = await useAsyncData(myBookingsKey, async () => {
  await loadMyBookings();
  return myBookings.value;
});
if (Array.isArray(firstMyBookings.value)) myBookings.value = firstMyBookings.value;
// Registered after both first loads, so setting rightNowOn from the server's availability above never asks twice.
watch(rightNowOn, loadMyBookings);

// X45 (Grant 2026-10-19): Requiring your attention, Memberships & passes and Your bookings are entries in one row
// under the header, each opening a dialog, instead of three sections. The row keeps its order; an entry is there
// only when it applies to this viewer (utils/clubPage.js says when).
const membershipsPath = computed(() => '/clubs/' + page.value.club.id + '/memberships');
// Grant 2026-11-02 ("If there is only one membership available, what is the point of the dialog?"): with exactly one
// type on offer every "see the list" link goes straight to that type's page; held, to the viewer's own membership.
const singleType = computed(() => membershipTypes.value.length === 1 ? membershipTypes.value[0] : null);
const membershipsTarget = computed(() => singleType.value ? membershipTypeTo(membershipsPath.value, singleType.value) : membershipsPath.value);
const singleTarget = computed(() => {
  if (!singleType.value) return null;
  const mine = heldMemberships.value.find(m => m.membershipTypeId === singleType.value.id) ?? heldMemberships.value[0];
  return mine?.id ? myMembershipTo(mine.id) : membershipsTarget.value;
});
// In the visitor preview the viewer is signed out for the row too: the visitor's "Sign in to see memberships &
// passes" entry shows (not live, like every button in the preview), the admin's own entries never (X73).
const entries = computed(() => clubEntries({
  admin: clubAdmin.value, applications: page.value?.applications?.length ?? 0,
  membershipsOn: membershipsOn.value, signedIn: isAuthenticated.value && !visitorPreview.value, preview: visitorPreview.value,
  membershipTypes: membershipTypes.value, held: heldMemberships.value, membershipsBlurb: membershipsBlurb.value, membershipsPath: membershipsPath.value,
  bookingsOn: rightNowOn.value, bookings: myBookings.value, membershipsLoading: !membershipTypesLoaded.value,
  hasLiveTypes: hasLiveMembershipTypes.value,
}));
const competitionTotal = computed(() => competitionCount(page.value));
const groups = computed(() => competitionGroups(page.value));

// The dialogs live in the address (?show=attention|memberships|bookings, router.replace both ways) so a refresh or a
// shared link opens the same one. They start closed and open once hydrated: a dialog opened during hydration throws
// in Vuetify's overlay and the direct load is a 500 (utils/queryFlags.js).
const nuxtApp = useNuxtApp();
const hydrated = ref(false);
onMounted(() => { whenHydrated(() => !!nuxtApp.isHydrating, () => { hydrated.value = true; }); });
const shown = computed(() => clubShowFromQuery(route.query));
const openShow = (show) => { router.replace({query: clubShowQuery(route.query, show)}); };
const openEntry = (e) => {
  if (visitorPreview.value) return;
  if (e.to) { router.push(e.to); return; }
  openShow(e.key);
};
const closeShown = () => { router.replace({query: clubShowQuery(route.query, null)}); };
const dialogFor = (key) => computed({
  get: () => hydrated.value && shown.value === key && clubShowAllowed(key, entries.value),
  set: (open) => { if (!open) closeShown(); },
});
const attentionOpen = dialogFor('attention');
const membershipsOpen = dialogFor('memberships');
const bookingsOpen = dialogFor('bookings');
// The last application reviewed, the last booking cancelled: the entry goes and its dialog closes with it, so the
// address doesn't keep a ?show= that nothing answers. Only once the entry was there: the row grows after hydration
// (types, then bookings) and a ?show=bookings link must not be dropped while its entry is still loading.
watch(entries, (list, before) => {
  if (shownEntryGone(shown.value, before, list)) closeShown();
});
// Grant 2026-11-02: ?show=memberships with one type on offer has no list to show: straight to that type's page (or the
// viewer's own membership), replacing the address so Back doesn't return to a dialog that never opens.
watch([shown, membershipTypesLoaded, hydrated], ([show, loaded, ready]) => {
  if (show !== 'memberships' || !loaded || !ready || visitorPreview.value || !singleTarget.value) return;
  router.replace(singleTarget.value);
}, {immediate: true});

// Top-right keeps only the admin action; everything else lives in the action bar.
const titleActions = computed(() => {
  const actions = [];
  if (clubAdmin.value) {
    actions.push({
      title: 'Edit club',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubDashboard', params: {clubId: page.value.club.id}}),
      color: 'info',
    });
  }
  return actions;
});
const {addListener, removeLocalListenersWithEvent} = useListenerService();
onMounted(() => {
  loadMembershipTypes();
  loadAccess();
  addListener("requestsUpdated", () => getPage(route.params.clubId));
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent("requestsUpdated");
});
</script>

<template>
  <base-page>
    <!-- Deleted (archived) club - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <v-container v-if="clubPrivate" class="large">
      <visitor-preview-banner v-if="visitorPreview"/>
      <frontend-header
          :club="page.club"
          :image-file-name="page.club.imageFileName"
          :title="page.club.name"
          title-icon="mdi-account-group"
      >
        <template #subTitle>
          <div class="club-header-sub">
            <span class="club-header-sub-text">
              {{ countNoun(page.club.playerCount, 'player') }}<template v-if="page.club.city"> · {{ page.club.city }}</template>
            </span>
          </div>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <v-row align="start">
        <v-col cols="12" :md="page.club.hasFixedAddress || rightNowOn ? 8 : 12">
          <private-notice reason="club" entity="club" :name="page.club.name" :join-slug="visibleJoinLink?.slug"
                          :unlisted="page.club.listedInSearch === false"
                          :facts="clubFacts(page.summary, page.club)" facts-title="What goes on here" :preview="visitorPreview"/>
        </v-col>
        <v-col v-show="page.club.hasFixedAddress || rightNowOn" cols="12" md="4">
          <!-- TEST_T2 M7: a club that takes bookings still lets a visitor book a playing area
               from its public page (the booking page itself is public, BOOKINGS_PLAN.md B10). -->
          <club-right-now-card :club-id="page.club.id" :preview="visitorPreview" :initial="firstAvailability" @loaded="rightNowOn = $event"/>
          <div v-if="page.club.hasFixedAddress" class="tl-card club-location-card">
            <a v-if="staticMapUrl" :href="directionsUrl" target="_blank" rel="noopener" class="club-map">
              <img :src="staticMapUrl" :alt="'Map of ' + page.club.name" class="club-map-img"/>
            </a>
            <a :href="directionsUrl || '#'" target="_blank" rel="noopener" class="club-location-body">
              <div class="club-location-info">
                <div class="club-location-line1">{{ page.club.addressLine1 }}</div>
                <div v-if="page.club.city || page.club.postCode" class="club-location-line2">
                  {{ [page.club.city, page.club.postCode].filter(Boolean).join(' ') }}
                </div>
              </div>
              <v-icon size="20" color="primary">mdi-directions</v-icon>
            </a>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-else-if="clubArchived" class="large">
      <archived-notice entity="club" :name="page.club.name"/>
    </v-container>
    <v-container v-else class="large">
      <visitor-preview-banner v-if="visitorPreview"/>

      <!-- Leave club confirmation (X74, R25 L11: the shared TlDialog, never a raw v-dialog) -->
      <tl-dialog v-model="showLeaveClubDialog" :title="`Leave ${page.club.name}?`" :max-width="400">
        <p v-if="leaveMembershipChoice">{{ leaveMembershipChoice }}</p>
        <p v-else>You'll be removed from the club and will need to request access again to rejoin.</p>
        <template #actions>
          <template v-if="leaveMembershipChoice">
            <v-btn variant="text" class="club-dialog-btn" @click="showLeaveClubDialog = false">Stay</v-btn>
            <v-btn variant="outlined" class="club-dialog-btn" :loading="leavePending" @click="handleLeaveClub('keep')">Keep {{ leaveChoicePronoun }} and leave</v-btn>
            <v-btn color="error" variant="flat" class="club-dialog-btn" :loading="leavePending" @click="handleLeaveClub('cancelAtPeriodEnd')">Cancel {{ leaveChoicePronoun }} and leave</v-btn>
          </template>
          <template v-else>
            <v-btn variant="text" class="club-dialog-btn" @click="showLeaveClubDialog = false">Cancel</v-btn>
            <v-btn color="error" variant="flat" class="club-dialog-btn" :loading="leavePending" @click="handleLeaveClub()">Leave club</v-btn>
          </template>
        </template>
      </tl-dialog>

      <!-- Header (consistent with ladder / tournament) -->
      <frontend-header
          :club="page.club"
          :image-file-name="page.club.imageFileName"
          :title="page.club.name"
          title-icon="mdi-account-group"
          :actions="titleActions"
      >
        <template #subTitle>
          <div class="club-header-sub">
            <span class="club-header-sub-text">
              {{ countNoun(page.club.playerCount, 'player') }}<template v-if="page.club.city"> · {{ page.club.city }}</template>
            </span>
            <span v-if="clubMember" class="tl-chip tl-chip--brand">Joined</span>
            <span v-else-if="clubAdmin" class="tl-chip tl-chip--brand">Admin</span>
          </div>
        </template>
      </frontend-header>

      <!-- Action bar (shared .tl-action-strip — same as ladder / tournament) -->
      <div class="tl-action-strip my-5">
        <!-- Share -->
        <short-link-share-wrapper
                    :join-reference-type="0" :join-reference-id="page?.club?.id" :can-share-join="clubAdmin">
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <!-- Chat (member) -->
        <button v-if="isAuthenticated && clubMember && !playersLocked" class="as" @click="handleChatClick" :disabled="entityChatLoading">
          <v-icon size="17" icon="mdi-forum"/>{{ page.club.conversationId ? 'Open Chat' : 'Start Chat' }}
        </button>

        <!-- Stats -->
        <router-link class="as" :to="{ name: 'ClubStats', params: { clubId: page.club.id } }">
          <v-icon size="17" icon="mdi-chart-line"/>Stats
        </router-link>

        <!-- Fixtures -->
        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [page.club.id] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>

        <!-- Admin: Email -->
        <template v-if="clubAdmin">
          <div class="vsep"></div>
          <router-link
              class="as"
              :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [page.club.id] }) } }"
          >
            <v-icon size="17" icon="mdi-email"/>Email
          </router-link>
        </template>

        <!-- More — Leave tucked away (member) -->
        <v-menu v-if="clubMember" location="bottom end">
          <template #activator="{ props }">
            <button class="as" v-bind="props"><v-icon size="17" icon="mdi-dots-horizontal"/>More</button>
          </template>
          <v-list density="compact">
            <v-list-item @click="showLeaveClubDialog = true">
              <template #prepend><v-icon size="18" color="error">mdi-exit-to-app</v-icon></template>
              <v-list-item-title class="text-error">Leave club</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Admin invite CTA — join-link state at a glance, opens the join-links dialog -->
      <join-settings-card
          v-if="clubAdmin"
          class="mb-5"
          :reference-type="0"
          :reference-id="page.club.id"
          :club-id="page.club.id"
          :entity-name="page.club.name"
          @changed="getPage(route.params.clubId)"
      />

      <!-- Pending request — already applied, awaiting approval -->
      <join-request-pending
          v-if="!clubMember && !clubAdmin && !visitorPreview && hasPendingRequest"
          :entity-name="page.club.name"
          :cancelling="cancelPending"
          @cancel="cancelRequest"
      />

      <!-- Join banner — non-members / guests with no pending request (mirrors the Flutter join block) -->
      <div v-else-if="!clubMember && !clubAdmin && visibleJoinLink" class="tl-card club-join-banner mb-5">
        <div class="club-join-ic"><v-icon color="primary">mdi-account-plus</v-icon></div>
        <div class="flex-fill">
          <div class="club-join-title">This club is open to join</div>
          <div class="club-join-sub">{{ autoJoin ? 'Join instantly' : 'An admin approves requests to join' }}</div>
        </div>
        <v-btn v-if="membershipsOn && hasLiveMembershipTypes !== false" variant="tonal" color="primary" rounded="pill" :to="membershipsTarget"
               prepend-icon="mdi-card-account-details-outline" :disabled="visitorPreview">Memberships &amp; passes</v-btn>
        <v-btn color="primary" rounded="pill" :to="'/invite/' + visibleJoinLink.slug" prepend-icon="mdi-account-plus"
               :disabled="visitorPreview">Join</v-btn>
      </div>

      <!-- X45: one row of entries (attention · memberships & passes · your bookings), each opening a dialog.
           Grant 2026-11-02: an entry with `more` (a membership held here) is a plain box: its title is the link to the
           viewer's own, stretched over the whole card (.club-entry-link::after), and "Get another" beside it is its
           own button opening the list, so a link never sits inside a link. -->
      <div v-if="entries.length" class="club-entries mb-5">
        <component
            :is="e.skeleton || e.more ? 'div' : e.to ? 'router-link' : 'button'"
            v-for="e in entries"
            :key="e.key"
            :to="e.to && !e.more ? e.to : undefined"
            :type="e.to || e.skeleton ? undefined : 'button'"
            :aria-busy="e.skeleton ? 'true' : undefined"
            class="tl-card club-entry"
            :class="['club-entry--' + e.tone, {'club-entry--skeleton': e.skeleton, 'club-entry--held': !!e.more}]"
            @click="e.to || e.skeleton ? undefined : openEntry(e)"
        >
          <div class="club-entry-ic"><v-icon size="20" :icon="e.icon"/></div>
          <div class="club-entry-body">
            <div class="club-entry-title">
              <router-link v-if="e.more" :to="e.to" class="club-entry-link">{{ e.title }}</router-link>
              <template v-else>{{ e.title }}</template>
              <span v-if="e.count" class="tl-chip club-entry-count" :class="'tl-chip--' + e.tone">{{ e.count }}</span>
            </div>
            <div v-if="e.skeleton" class="club-entry-sub" aria-hidden="true"><span class="club-entry-skel"/></div>
            <div v-else-if="e.sub" class="club-entry-sub">{{ e.sub }}</div>
          </div>
          <button v-if="e.more" type="button" class="club-entry-more" :aria-label="e.more.label + ' membership or pass'" @click="openShow(e.more.show)">{{ e.more.label }}</button>
          <v-icon v-else size="18" class="club-entry-chev" icon="mdi-chevron-right"/>
        </component>
      </div>

      <!-- Requiring your attention: the join requests, reviewed from here -->
      <tl-dialog v-model="attentionOpen" title="Requiring your attention" :max-width="560" flush>
        <div class="club-dialog-list">
          <access-application-cell
              v-for="application in page.applications"
              :key="application.id"
              :request="application"
          />
        </div>
        <template #actions>
          <v-btn variant="outlined" class="club-dialog-btn" @click="attentionOpen = false">Close</v-btn>
        </template>
      </tl-dialog>

      <!-- Memberships & passes (D31): what the club offers; rows open the type -->
      <tl-dialog v-model="membershipsOpen" title="Memberships &amp; passes" :max-width="560" flush>
        <div class="club-memberships-blurb">
          <div class="club-join-ic"><v-icon color="primary">mdi-card-account-details-outline</v-icon></div>
          <div class="flex-fill">
            <div class="club-join-title">{{ membershipsBlurb }}</div>
            <div class="club-join-sub">Set by {{ page.club.name }} · yours live under Profile, then Memberships &amp; passes</div>
          </div>
        </div>
        <div class="club-dialog-list club-memberships-list">
          <membership-type-row v-for="t in membershipTypes" :key="t.id" :type="t" :to="membershipsPath + '/' + t.id" :tag="heldMemberships.some(m => m.membershipTypeId === t.id) ? 'Yours' : null"/>
        </div>
        <template #actions>
          <v-btn variant="outlined" class="club-dialog-btn" @click="membershipsOpen = false">Close</v-btn>
          <v-btn color="primary" variant="flat" class="club-dialog-btn" :to="membershipsTarget">See memberships &amp; passes</v-btn>
        </template>
      </tl-dialog>

      <!-- Your bookings at this club (BOOKINGS_PLAN.md B14): cancel, release, pay, from here -->
      <tl-dialog v-model="bookingsOpen" title="Your bookings" :max-width="560" flush>
        <div class="club-dialog-list">
          <my-bookings-list :club-id="page.club.id" :with-past="false" personal bare @changed="loadMyBookings"/>
        </div>
        <template #actions>
          <v-btn variant="outlined" class="club-dialog-btn" @click="bookingsOpen = false">Close</v-btn>
          <v-btn color="primary" variant="flat" class="club-dialog-btn" :to="{path: '/your-profile', query: {tab: 'bookings'}}">See all your bookings</v-btn>
        </template>
      </tl-dialog>

      <!-- Two-column layout -->
      <v-row align="start">

        <!-- ========== LEFT COLUMN ========== -->
        <v-col cols="12" md="8">

          <!-- Competitions first (X45), grouped by format so a long list stays scannable; nothing is left out -->
          <template v-if="groups.length">
            <div class="tl-section-head">
              <h2>Competitions<span class="club-section-count"> · {{ competitionTotal }}</span></h2>
            </div>
            <div class="tl-card club-comp-list">
              <template v-for="g in groups" :key="g.key">
                <div v-if="groups.length > 1" class="club-comp-group">
                  <v-icon size="14" :icon="g.icon"/>
                  <span>{{ g.name }}</span>
                  <span class="club-comp-group-n">{{ g.items.length }}</span>
                </div>
                <template v-if="g.key === 'league'">
                  <league-clickable-cell v-for="league in g.items" :key="league.id" :league="league" :time-zone="page.club.effectiveTimeZone || page.club.timeZone" class="club-comp-item"/>
                </template>
                <template v-else-if="g.key === 'americano'">
                  <americano-clickable-cell v-for="americano in g.items" :key="americano.id" :americano="americano" :time-zone="page.club.effectiveTimeZone || page.club.timeZone" class="club-comp-item"/>
                </template>
                <template v-else-if="g.key === 'ladder'">
                  <ladder-clickable-cell v-for="ladder in g.items" :key="ladder.id" :ladder="ladder" class="club-comp-item"/>
                </template>
                <template v-else-if="g.key === 'tournament'">
                  <tournament-clickable-cell v-for="tournament in g.items" :key="tournament.id" :tournament="tournament" class="club-comp-item"/>
                </template>
                <template v-else-if="g.key === 'meet'">
                  <meet-clickable-cell v-for="meet in g.items" :key="meet.id" :meet="meet" class="club-comp-item"/>
                </template>
              </template>
            </div>
          </template>

          <!-- Members — full list, keyword-searchable -->
          <div class="tl-section-head club-members-head">
            <h2>Players<template v-if="playersLocked"> · {{ page.club.playerCount }}</template></h2>
            <v-text-field
                v-if="!playersLocked"
                v-model="memberSearch"
                placeholder="Search players"
                prepend-inner-icon="mdi-magnify"
                density="compact"
                hide-details
                clearable
                class="club-member-search"
            />
          </div>
          <!-- D47: no membership, past the grace period: a plain count and the types on offer -->
          <div v-if="playersLocked" class="tl-card club-players-locked">
            <div class="club-join-ic"><v-icon color="primary">mdi-account-lock-outline</v-icon></div>
            <div class="flex-fill">
              <div class="club-join-title">{{ playersNotice.line }}</div>
              <div class="club-join-sub">{{ countNoun(page.club.playerCount, 'player') }}. With a membership or pass you see them, the club chats and other players by name. Competitions you enter still show who you play.</div>
              <div v-if="typesOnOffer.length" class="club-players-types">
                <router-link v-for="t in typesOnOffer" :key="t.id" class="tl-chip tl-chip--brand club-players-type"
                             :to="'/clubs/' + page.club.id + '/memberships/' + t.id">
                  <v-icon size="12">{{ t.displayAs === DISPLAY_AS.pass ? 'mdi-ticket-confirmation-outline' : 'mdi-card-account-details-outline' }}</v-icon>
                  {{ t.name }}<template v-if="t.isFree"> · Free</template>
                </router-link>
              </div>
            </div>
            <v-btn variant="tonal" color="primary" rounded="pill" size="small" :to="membershipsTarget">See memberships &amp; passes</v-btn>
          </div>
          <!-- D47 grace period: still visible, a soft reminder with the end date -->
          <div v-else-if="playersNotice.state === 'grace'" class="tl-card club-players-grace">
            <v-icon size="18" color="warning">mdi-clock-outline</v-icon>
            <span class="flex-fill">{{ playersNotice.line }}</span>
            <router-link :to="membershipsTarget">See memberships &amp; passes</router-link>
          </div>
          <template v-if="playersLocked"/>
          <no-results v-else-if="page.players.length === 0">
            <template v-if="clubMember">No players in this club yet</template>
            <template v-else>Only players with a public profile are shown to people outside the club.</template>
          </no-results>
          <no-results v-else-if="filteredMembers.length === 0">
            No players match "{{ memberSearch }}"
          </no-results>
          <template v-else>
            <div class="tl-card club-members-list">
              <!-- Standard player card: consistent avatar/name + the expandable
                   player dialog (message, profile) instead of a bare link.
                   The first 50 only until "Show all" (the search still covers everyone). -->
              <div
                  v-for="player in shownMembers"
                  :key="player.id"
                  class="club-member-row"
                  role="button"
                  tabindex="0"
                  :aria-label="'Open ' + (player.fullName || 'player')"
                  @click="openMemberRow($event, player.id)"
                  @keydown.enter.prevent="openMemberRow($event, player.id)"
                  @keydown.space.prevent="openMemberRow($event, player.id)"
              >
                <player-card :ref="el => setMemberCard(player.id, el)" :player="player" class="club-member-name-col"/>
                <span v-if="page.club.adminPlayerIds && page.club.adminPlayerIds.includes(player.id)"
                      class="tl-chip">Admin</span>
                <span v-else-if="user && player.id === user.playerId" class="tl-chip tl-chip--brand">You</span>
              </div>
            </div>
            <div v-if="membersCapped" class="club-members-more">
              <span class="club-members-more-text">{{ showingLine }}</span>
              <v-btn variant="tonal" color="primary" rounded="pill" size="small" @click="showAllPlayers = true">{{ showAllLabel }}</v-btn>
            </div>
          </template>

        </v-col>

        <!-- ========== RIGHT COLUMN (sticky) ========== -->
        <v-col cols="12" md="4">
          <div class="club-side-sticky">

            <!-- Stats first (X45) -->
            <div class="tl-section-head">
              <h2>Club stats</h2>
              <router-link :to="{ name: 'ClubStats', params: { clubId: page.club.id } }">Full stats</router-link>
            </div>
            <div class="club-stat-grid">
              <div class="club-stat-card">
                <div class="club-stat-v">{{ page.fixtureCount }}</div>
                <div class="club-stat-k">Matches played</div>
              </div>
              <div class="club-stat-card">
                <div class="club-stat-v">{{ competitionTotal }}</div>
                <div class="club-stat-k">Competitions</div>
              </div>
            </div>

            <!-- Playing area availability (BOOKINGS_PLAN.md §6, X42's title): the playing areas and what each is
                 doing, and Book. Shown only when the club takes bookings (the card asks the API). -->
            <club-right-now-card :club-id="page.club.id" :preview="visitorPreview" :initial="firstAvailability" @loaded="rightNowOn = $event"/>

            <!-- Location / map card -->
            <div v-if="page.club.hasFixedAddress" class="tl-card club-location-card">
              <a v-if="staticMapUrl" :href="directionsUrl" target="_blank" rel="noopener" class="club-map">
                <img :src="staticMapUrl" :alt="'Map of ' + page.club.name" class="club-map-img"/>
              </a>
              <a :href="directionsUrl || '#'" target="_blank" rel="noopener" class="club-location-body">
                <div class="club-location-info">
                  <div class="club-location-line1">{{ page.club.addressLine1 }}</div>
                  <div v-if="page.club.city || page.club.postCode" class="club-location-line2">
                    {{ [page.club.city, page.club.postCode].filter(Boolean).join(' ') }}
                  </div>
                </div>
                <v-icon color="primary" size="20">mdi-directions</v-icon>
              </a>
            </div>

            <!-- Recent results -->
            <div class="tl-section-head">
              <h2>Recent results</h2>
              <a href="#"
                 @click.prevent="router.push({ name: 'FixtureSearch', query: { clubIds: [page.club.id] } })">
                See all
              </a>
            </div>
            <crud-list-loader
                path="api/v2/fixtures"
                :listeners="['fixtureUpdated']"
                :per-page="3"
                :custom-filters="{ clubIds: [page.club.id], isPlayed : true }"
            >
              <template #loader>
                <div class="tl-card">
                  <v-skeleton-loader type="list-item-two-line" />
                  <v-skeleton-loader type="list-item-two-line" />
                  <v-skeleton-loader type="list-item-two-line" />
                </div>
              </template>
              <template #empty>
                <no-results>{{ t("club_no_fixtures") }}</no-results>
              </template>
              <template #default="{ items: latestFixtures }">
                <no-results v-if="latestFixtures.length === 0">{{ t("club_no_fixtures") }}</no-results>
                <div v-else class="club-results-list">
                  <fixture-result-row
                      v-for="fixture in latestFixtures"
                      :key="fixture.id"
                      :fixture="fixture"
                  />
                </div>
              </template>
            </crud-list-loader>

          </div>
        </v-col>

      </v-row>

    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
/* ---- Header subtitle (members · city + membership chip) ---- */
.club-header-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.club-header-sub-text {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

/* ---- Entries row (X45): attention · memberships & passes · your bookings ---- */
.club-entries {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

/* Grant 2026-11-02: whatever entries are there share the whole row (one fills it, two halve it, three third it);
   the old 560px cap left a gap beside a lone card. The skeleton is the same box, so nothing jumps when it fills. */
.club-entry {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 12px;
  text-align: left;
  text-decoration: none;
  color: inherit;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }

  &--warn {
    border-color: rgba(var(--v-theme-warning), 0.45);

    .club-entry-ic {
      background: rgb(var(--v-theme-warningTint));
      color: rgb(var(--v-theme-warning));
    }

    &:hover {
      border-color: rgb(var(--v-theme-warning));
    }
  }

  /* Grant 2026-11-02: a membership held here reads as a status: the success tint, never a call to action */
  &--ok {
    border-color: rgba(var(--v-theme-success), 0.45);

    .club-entry-ic {
      background: rgb(var(--v-theme-successTint));
      color: rgb(var(--v-theme-success));
    }

    &:hover {
      border-color: rgb(var(--v-theme-success));
    }
  }
}

/* Grant 2026-11-02: the held entry's title link covers the card; "Get another" sits above that cover */
.club-entry--held {
  position: relative;
}

.club-entry-link {
  color: inherit;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
}

.club-entry-more {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  padding: 4px 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

/* X219 (R89 P-1): the memberships entry's place while its types load: the same box, a bar where the names go */
.club-entry--skeleton {
  cursor: default;

  &:hover {
    border-color: rgb(var(--v-theme-borderLight));
  }
}

.club-entry-skel {
  display: inline-block;
  width: 60%;
  height: 0.8em;
  vertical-align: middle;
  border-radius: 4px;
  background: rgb(var(--v-theme-borderLight));
}

.club-entry-ic {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.club-entry-body {
  flex: 1;
  min-width: 0;
}

.club-entry-title {
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.club-entry-count {
  font-size: 10.5px;
  padding: 1px 7px;
}

.club-entry-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.club-entry-chev {
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

/* R22 Low 1: three across squeezed to 237px at 768 (titles wrapped, lines cut). From 899 down the entries go two across
   (a third on its own full row); from 680 down they stack (X61, R23b L1: at 600 two across was 278px each and
   "Memberships & passes" wrapped), so every title stays on one line and its sub line reads. Both bounds inclusive. */
@media (max-width: 899px) {
  .club-entry {
    flex-basis: calc(50% - 6px);
  }
}

@media (max-width: 680px) {
  .club-entry {
    flex-basis: 100%;
  }
}

/* ---- The dialogs' lists (rows carry their own dividers) ---- */
.club-dialog-list {
  min-width: 0;
}

/* R22 Low 4: the footer wraps cleanly on a phone. The TlDialog foot wraps; when Close and the primary action can't
   share the 342px dialog they each take a full row (Close above, the primary action below), never Close alone at
   the right with the primary hanging under it. */
@media (max-width: 480px) {
  .club-dialog-btn {
    flex: 1 1 auto;
  }
}

.club-memberships-blurb {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.club-memberships-list {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

/* ---- Join banner (non-members) ---- */
.club-join-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.club-join-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgb(var(--v-theme-brandTint));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.club-join-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.club-join-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

/* ---- Location map ---- */
.club-map {
  display: block;
  line-height: 0;
}

.club-map-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

/* sticky side column (the v-col handles the grid; this just pins its content) */
.club-side-sticky {
  position: sticky;
  top: 84px; /* topbar height + padding */
}

/* ---- Competitions ---- */
.club-section-count {
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
}

/* a format's header inside the list: small caps, its icon, the count */
.club-comp-group {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px 6px;
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: rgb(var(--v-theme-light));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:not(:first-child) {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  .v-icon {
    color: rgb(var(--v-theme-textSubtle));
  }
}

.club-comp-group-n {
  font-weight: 600;
  opacity: 0.8;
}

/* Override ClickableCell's own .tl-card + margin so items run flush inside our container */
.club-comp-list {
  padding: 0;

  /* strip each competition cell's own card chrome — our parent card provides the shell */
  :deep(.comp-row) {
    margin-bottom: 0;
    border-radius: 0 !important;
    border: none !important;
    border-bottom: 1px solid rgb(var(--v-theme-borderLight)) !important;
    overflow: visible;

    &:last-child {
      border-bottom: none !important;
    }
  }

  /* the wrapper component adds mb-3 — zero it out */
  :deep(> *) {
    margin-bottom: 0 !important;
  }
}

/* ---- Members ---- */
/* search box sits opposite the heading (override the section-head baseline) */
.club-members-head {
  align-items: center;
  gap: 12px;
}

.club-member-search {
  flex: 0 1 220px;
  max-width: 220px;
}

/* pill shape to match the members-toolbar search (override the global 12px field radius) */
.club-member-search :deep(.v-field) {
  border-radius: 999px !important;
}

.club-member-search :deep(.v-field__outline__start) {
  border-radius: 999px 0 0 999px !important;
}

.club-member-search :deep(.v-field__outline__end) {
  border-radius: 0 999px 999px 0 !important;
}

/* full list scrolls within a fixed height rather than pushing the page down */
.club-members-list {
  max-height: 420px;
  overflow-y: auto;
}

/* under the cut list, outside the scroll: how many are shown and the Show all control */
.club-members-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.club-members-more-text {
  font-size: 13px;
  color: rgb(var(--v-theme-textSubtle));
}

.club-member-row {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  cursor: pointer;

  & + & {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }
}

.club-member-name-col {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.club-member-face {
  width: 32px !important;
  height: 32px !important;
  border-radius: 50% !important;
  flex-shrink: 0;
}

.club-member-face--fallback {
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
}

.club-member-nm {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- Location card ---- */
.club-location-card {
  overflow: hidden;
}

.club-location-body {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  text-decoration: none;
  color: inherit;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:hover { background: rgb(var(--v-theme-light)); }
}

.club-location-info {
  flex: 1;
  min-width: 0;
}

.club-location-line1 {
  font-size: 13px;
  font-weight: 600;
}

.club-location-line2 {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

/* ---- Stats grid ---- */
.club-stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.club-stat-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 14px 16px;
}

.club-stat-v {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.club-stat-k {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 2px;
}

/* ---- Recent results ---- */
.club-results-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* D47: players only for membership holders */
.club-players-locked {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  flex-wrap: wrap;
}

.club-players-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.club-players-type {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
}

.club-players-grace {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 10px;
  font-size: 13px;
  border-color: rgba(var(--v-theme-warning), 0.35);
  flex-wrap: wrap;
}
</style>
